import { z } from "zod";

import type {
  PrimeUiComponentCandidate,
  PrimeUiComponentCandidateConstraints,
  PrimeUiComponentCandidateLayout,
  PrimeUiComponentCandidateOperation,
  PrimeUiComponentCandidateRequestEcho,
  PrimeUiComponentCandidatesInput,
  PrimeUiComponentCandidatesResult,
  PrimeUiComponentPropsRenderCheck,
  PrimeUiComponentPropsValidateInput,
  PrimeUiComponentPropsValidateResult,
  PrimeUiComponentPropsValidationError,
  PrimeUiIssueReportSubmitResult,
  PrimeUiComponentExportComponent,
  PrimeUiComponentExportManifest,
  PrimeUiComponentExportPage,
  PrimeUiComponentExportSelectedBlock,
  PrimeUiManagedSearchEnvironmentResult,
  PrimeUiManagedSearchIndex,
  PrimeUiManagedSearchJob,
  PrimeUiManagedSearchLastError,
  PrimeUiManagedSearchOverviewResult,
  PrimeUiManagedSearchRuntimeApiMutationResult,
  PrimeUiManagedSearchQueryProtectionState,
  PrimeUiManagedSearchRelevanceSettingsSummary,
  PrimeUiManagedSearchRoot,
  PrimeUiManagedSearchRuntime,
  PrimeUiManagedSearchRuntimeDeleteResult,
  PrimeUiManagedSearchRuntimeModel,
  PrimeUiManagedSearchRuntimeMutationResult,
  PrimeUiManagedSearchSource,
  PrimeUiManagedSearchSyncRun,
  PrimeUiPageGenerationType,
  PrimeUiProjectApiPageResource,
  PrimeUiProjectApiPagesResult,
  PrimeUiProjectApiVariantResource,
  PrimeUiProjectApiVariantResult,
  PrimeUiProjectApiVariantSummaryResult,
  PrimeUiProjectApiVariantsResult,
  PrimeUiProjectApiVariantSummary,
  PrimeUiProjectApiWireframeStatus,
  PrimeUiCreateExportResult,
  PrimeUiExportManifest,
  PrimeUiExportStatus,
  PrimeUiExportedExportable,
  PrimeUiProjectDescriptionResult,
  PrimeUiProjectExportable,
  PrimeUiProjectInfo,
  PrimeUiProjectPage,
  PrimeUiProjectPageComponent,
  PrimeUiProjectPageDetailsResult,
} from "../types.js";

const primeUiPageSlugInputSchema = z
  .string()
  .refine((value) => value.trim().length > 0, {
    message: "pageSlug must be a non-empty string",
  });

export const primeUiExportStatusSchema: z.ZodType<PrimeUiExportStatus> = z.enum(
  ["in_progress", "completed", "failed"],
);

export const primeUiProjectPageObjectSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  pageType: z.string(),
  isReadyToExport: z.boolean(),
  pagePath: z.string(),
  componentsPath: z.string(),
});

export const primeUiProjectPageSchema: z.ZodType<PrimeUiProjectPage> =
  primeUiProjectPageObjectSchema;

export const primeUiProjectExportableSchema: z.ZodType<PrimeUiProjectExportable> =
  z.object({
    key: z.string(),
    description: z.string(),
    isReadyToExport: z.boolean(),
  });

export const primeUiExportSummarySchema = z.object({
  total: z.number(),
  successful: z.number(),
  failed: z.number(),
});

export const primeUiExportableManifestSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  files: z.array(z.string()),
});

export const primeUiExportedExportableSchema: z.ZodType<PrimeUiExportedExportable> =
  z.object({
    key: z.string(),
    isReadyToExport: z.boolean(),
    manifest: primeUiExportableManifestSchema,
  });

export const primeUiExportPageManifestSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  files: z.array(z.string()),
});

export const primeUiExportPageSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  slug: z.string(),
  pageType: z.string(),
  isReadyToExport: z.literal(true),
  pagePath: z.string(),
  componentsPath: z.string(),
  manifest: primeUiExportPageManifestSchema,
});

export const primeUiPostExportPayloadExportSchema = z.object({
  id: z.string(),
  status: primeUiExportStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  expiresAt: z.string().datetime({ offset: true }).nullable(),
  summary: primeUiExportSummarySchema,
  exportables: z.array(primeUiExportedExportableSchema),
});

export const primeUiComponentExportComponentSchema: z.ZodType<PrimeUiComponentExportComponent> =
  z.object({
    componentId: z.string(),
    name: z.string(),
    pageType: z.string(),
    family: z.string(),
    defaultPropsAvailable: z.boolean(),
    isReadyToExport: z.boolean(),
  });

export const primeUiComponentExportPageSchema: z.ZodType<PrimeUiComponentExportPage> =
  z.object({
    id: z.string(),
    title: z.string().optional(),
    slug: z.string(),
    pageType: z.string(),
    isReadyToExport: z.boolean(),
    manifest: primeUiExportPageManifestSchema,
  });

export const primeUiComponentExportSelectedBlockSchema: z.ZodType<PrimeUiComponentExportSelectedBlock> =
  z.object({
    blockId: z.string(),
    componentId: z.string(),
    contentKey: z.string(),
    occurrenceIndex: z.number().int().positive(),
    props: z.record(z.unknown()).nullable(),
    slot: z.string().nullable(),
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project.
 * Primary Studio runtime entrypoint: apps/studio/src/tanstack/routes/api.v1.project.ts
 * Legacy migration reference: apps/studio/src/app/api/v1/project/route.ts
 */
export const primeUiProjectInfoSchema: z.ZodType<PrimeUiProjectInfo> = z.object(
  {
    projectId: z.string(),
    projectName: z.string(),
    metadata: z.record(z.unknown()),
    exportables: z.array(primeUiProjectExportableSchema),
    pages: z.array(primeUiProjectPageSchema),
  },
);

export const primeUiProjectPageComponentSchema: z.ZodType<PrimeUiProjectPageComponent> =
  z.object({
    blockId: z.string(),
    componentId: z.string(),
    componentGroup: z.string(),
    slot: z.string().nullable(),
    props: z.record(z.unknown()).nullable(),
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project/page?slug=...
 * Primary Studio runtime entrypoint: apps/studio/src/tanstack/routes/api.v1.project.page.ts
 * Legacy migration reference: apps/studio/src/app/api/v1/project/page/route.ts
 */
export const primeUiProjectPageDetailsSchema: z.ZodType<PrimeUiProjectPageDetailsResult> =
  z.object({
    page: primeUiProjectPageObjectSchema.extend({
      pageInstruction: z.string().nullable(),
    }),
    variant: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullable(),
    components: z.array(primeUiProjectPageComponentSchema).nullable(),
  });

export const primeUiProjectDescriptionSchema: z.ZodType<PrimeUiProjectDescriptionResult> =
  z.object({
    projectDescription: z.string(),
  });

export const primeUiPageGenerationTypeSchema: z.ZodType<PrimeUiPageGenerationType> =
  z.enum([
    "landing",
    "pricing",
    "blogIndex",
    "blogPost",
    "contact-us",
    "docs",
    "legal",
  ]);

export const primeUiComponentCandidateOperationSchema: z.ZodType<PrimeUiComponentCandidateOperation> =
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("append"),
    }),
    z.object({
      type: z.literal("insert"),
      index: z.number().int(),
    }),
    z.object({
      type: z.literal("replace"),
      index: z.number().int(),
    }),
  ]);

export const primeUiComponentCandidateConstraintsSchema: z.ZodType<PrimeUiComponentCandidateConstraints> =
  z.object({
    spreadDegree: z.enum(["any-group", "same-group", "same-family"]).optional(),
    allowedGroups: z.array(z.string()).optional(),
    excludedGroups: z.array(z.string()).optional(),
    excludeComponentIds: z.array(z.string()).optional(),
  });

export const primeUiComponentCandidatesInputSchema: z.ZodType<PrimeUiComponentCandidatesInput> =
  z.object({
    pageSlug: primeUiPageSlugInputSchema,
    pageType: primeUiPageGenerationTypeSchema,
    blocks: z.array(
      z.object({
        componentId: z.string(),
      }),
    ),
    operation: primeUiComponentCandidateOperationSchema,
    count: z.number().int().positive().optional(),
    constraints: primeUiComponentCandidateConstraintsSchema.optional(),
  });

export const primeUiComponentCandidateRequestEchoSchema: z.ZodType<PrimeUiComponentCandidateRequestEcho> =
  z.object({
    pageSlug: z.string(),
    pageType: z.string(),
    operation: primeUiComponentCandidateOperationSchema,
    count: z.number(),
  });

const requiredUnknownSchema = z.custom<unknown>((value) => value !== undefined);

const primeUiComponentCandidateLayoutObjectSchema = z.object({
  container: requiredUnknownSchema,
  textAlignment: requiredUnknownSchema,
  bottomWeight: requiredUnknownSchema,
  entryWidth: requiredUnknownSchema,
  exitWidth: requiredUnknownSchema,
  orientation: requiredUnknownSchema,
  structure: requiredUnknownSchema,
});

export const primeUiComponentCandidateLayoutSchema: z.ZodType<PrimeUiComponentCandidateLayout> =
  z.custom<PrimeUiComponentCandidateLayout>(
    (value) =>
      primeUiComponentCandidateLayoutObjectSchema.safeParse(value).success,
  );

const primeUiComponentCandidateObjectSchema = z.object({
  componentId: z.string(),
  name: z.string(),
  group: z.string(),
  familyId: z.string(),
  uxScore: z.number(),
  insertionScore: z.number(),
  followingScore: z.number(),
  layout: primeUiComponentCandidateLayoutSchema,
  defaultProps: requiredUnknownSchema,
  exportReady: z.boolean(),
  copyHints: z.object({
    deliveryToolchain: z.array(z.string()),
  }),
  description: z.string().nullable().optional(),
  functionality: z.string().nullable().optional(),
  impression: z.string().nullable().optional(),
  visualStyle: z.unknown().optional(),
  compactSchema: z.string().nullable().optional(),
  jsonSchema: z.record(z.unknown()).nullable().optional(),
});

export const primeUiComponentCandidateSchema: z.ZodType<PrimeUiComponentCandidate> =
  z.custom<PrimeUiComponentCandidate>(
    (value) => primeUiComponentCandidateObjectSchema.safeParse(value).success,
  );

/**
 * Consumer-side runtime contract for POST /api/v1/component-planning/candidates.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/routes/api/v1/component-planning/candidates/route.ts
 */
export const primeUiComponentCandidatesResponseSchema: z.ZodType<PrimeUiComponentCandidatesResult> =
  z.object({
    request: primeUiComponentCandidateRequestEchoSchema,
    candidates: z.array(primeUiComponentCandidateSchema),
  });

export const primeUiComponentPropsValidateInputSchema: z.ZodType<PrimeUiComponentPropsValidateInput> =
  z.object({
    blockId: z.string().refine((value) => value.trim().length > 0, {
      message: "blockId must be a non-empty string",
    }),
    pageSlug: primeUiPageSlugInputSchema,
    componentId: z.string(),
    props: z.record(z.unknown()),
  });

export const primeUiComponentPropsValidationErrorSchema: z.ZodType<PrimeUiComponentPropsValidationError> =
  z.object({
    path: z.string(),
    message: z.string(),
  });

export const primeUiComponentPropsRenderCheckSchema: z.ZodType<PrimeUiComponentPropsRenderCheck> =
  z.object({
    attempted: z.boolean(),
    passed: z.boolean(),
    message: z.string(),
  });

/**
 * Consumer-side runtime contract for POST /api/v1/component-planning/props/validate.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/routes/api/v1/component-planning/props/validate/route.ts
 */
export const primeUiComponentPropsValidateResponseSchema: z.ZodType<PrimeUiComponentPropsValidateResult> =
  z.object({
    valid: z.boolean(),
    normalizedProps: z.record(z.unknown()).nullable(),
    propsValidationId: z.string().nullable(),
    block: z
      .object({
        blockId: z.string(),
        componentId: z.string(),
        contentKey: z.string(),
        props: z.record(z.unknown()),
      })
      .nullable(),
    errors: z.array(primeUiComponentPropsValidationErrorSchema),
    hints: z.array(z.string()),
    renderCheck: primeUiComponentPropsRenderCheckSchema,
  });

export const primeUiProjectApiWireframeStatusSchema: z.ZodType<PrimeUiProjectApiWireframeStatus> =
  z.enum(["pending", "generating", "ready", "error"]);

export const primeUiProjectApiVariantSummarySchema: z.ZodType<PrimeUiProjectApiVariantSummary> =
  z.object({
    id: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    isGenerating: z.boolean(),
  });

export const primeUiProjectApiPageResourceSchema: z.ZodType<PrimeUiProjectApiPageResource> =
  z.object({
    id: z.string(),
    title: z.string(),
    slug: z.string(),
    pageType: primeUiPageGenerationTypeSchema,
    isReadyToExport: z.boolean(),
    wireframeStatus: primeUiProjectApiWireframeStatusSchema,
    variants: z.array(primeUiProjectApiVariantSummarySchema),
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project/pages and
 * GET /api/v1/project/pages/:pageId, and for POST responses that return pages.
 * Primary Studio runtime entrypoints:
 * - apps/studio/src/tanstack/routes/api.v1.project.pages.ts
 * - apps/studio/src/tanstack/routes/api.v1.project.pages.$pageId.ts
 * - apps/studio/src/tanstack/routes/api.v1.project.pages.$pageId.active-variant.ts
 * Legacy migration references:
 * - apps/studio/src/app/api/v1/project/pages/route.ts
 * - apps/studio/src/app/api/v1/project/pages/[pageId]/route.ts
 * - apps/studio/src/app/api/v1/project/pages/[pageId]/active-variant/route.ts
 */
export const primeUiProjectApiPagesResponseSchema: z.ZodType<PrimeUiProjectApiPagesResult> =
  z.object({
    pages: z.array(primeUiProjectApiPageResourceSchema),
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project/pages/:pageId/variants.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/tanstack/routes/api.v1.project.pages.$pageId.variants.ts
 * Legacy migration reference:
 * apps/studio/src/app/api/v1/project/pages/[pageId]/variants/route.ts
 */
export const primeUiProjectApiVariantsResponseSchema: z.ZodType<PrimeUiProjectApiVariantsResult> =
  z.object({
    variants: z.array(primeUiProjectApiVariantSummarySchema),
  });

export const primeUiProjectApiVariantResourceSchema: z.ZodType<PrimeUiProjectApiVariantResource> =
  z.object({
    id: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    isGenerating: z.boolean(),
    components: z.array(primeUiProjectPageComponentSchema),
  });

/**
 * Consumer-side runtime contract for POST /api/v1/project/pages/:pageId/variants.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/tanstack/routes/api.v1.project.pages.$pageId.variants.ts
 * Legacy migration reference:
 * apps/studio/src/app/api/v1/project/pages/[pageId]/variants/route.ts
 */
export const primeUiProjectApiVariantSummaryResponseSchema: z.ZodType<PrimeUiProjectApiVariantSummaryResult> =
  z.object({
    variant: primeUiProjectApiVariantSummarySchema,
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project/pages/:pageId/variants/:variantId.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/tanstack/routes/api.v1.project.pages.$pageId.variants.$variantId.ts
 * Legacy migration reference:
 * apps/studio/src/app/api/v1/project/pages/[pageId]/variants/[variantId]/route.ts
 */
export const primeUiProjectApiVariantResponseSchema: z.ZodType<PrimeUiProjectApiVariantResult> =
  z.object({
    variant: primeUiProjectApiVariantResourceSchema,
  });

/**
 * Consumer-side runtime contract for POST /api/v1/issue-reports.
 * Primary Studio runtime entrypoint: apps/studio/src/tanstack/routes/api.v1.issue-reports.ts
 * Legacy migration reference: apps/studio/src/app/api/v1/issue-reports/route.ts
 */
export const primeUiIssueReportSubmitResponseSchema: z.ZodType<PrimeUiIssueReportSubmitResult> =
  z.object({
    accepted: z.literal(true),
    ticketId: z.string(),
  });

const nullableDateStringSchema = z.string().nullable();

export const primeUiManagedSearchLastErrorSchema: z.ZodType<PrimeUiManagedSearchLastError> =
  z.object({
    at: nullableDateStringSchema,
    code: z.string().nullable(),
    message: z.string(),
    scope: z.enum(["project", "index", "job", "sync-run"]),
  });

export const primeUiManagedSearchRuntimeModelSchema: z.ZodType<PrimeUiManagedSearchRuntimeModel> =
  z.object({
    additionalRuntimesSupported: z.literal(true),
    defaultRuntimeAutoWired: z.literal(true),
    defaultRuntimeSlug: z.literal("prod"),
    mode: z.literal("runtime-first"),
    panelCompatibility: z.literal("disabled"),
    stagingRuntimeAutoWired: z.literal(false),
    stagingRuntimeSlug: z.literal("staging"),
  });

export const primeUiManagedSearchRuntimeSchema: z.ZodType<PrimeUiManagedSearchRuntime> =
  z.object({
    createdAt: z.string(),
    currentMeilisearchIndexUid: z.string().nullable(),
    currentQueryId: z.string().nullable(),
    id: z.string(),
    lastErrorSummary: z.string().nullable(),
    lastFailedSyncAt: nullableDateStringSchema,
    lastSuccessfulSyncAt: nullableDateStringSchema,
    name: z.string(),
    slug: z.string(),
    status: z.enum([
      "bootstrapping",
      "active",
      "indexing",
      "failed",
      "inactive",
    ]),
    updatedAt: z.string(),
  });

export const primeUiManagedSearchRootSchema: z.ZodType<PrimeUiManagedSearchRoot> =
  z.object({
    bootstrapCompletedAt: nullableDateStringSchema,
    createdAt: nullableDateStringSchema,
    defaultIndexId: z.string().nullable(),
    defaultSearchId: z.string().nullable(),
    enabled: z.boolean(),
    id: z.string().nullable(),
    lastErrorSummary: z.string().nullable(),
    lastFailedSyncAt: nullableDateStringSchema,
    lastSuccessfulSyncAt: nullableDateStringSchema,
    status: z.enum(["inactive", "active", "degraded"]),
    updatedAt: nullableDateStringSchema,
  });

export const primeUiManagedSearchSourceSchema: z.ZodType<PrimeUiManagedSearchSource> =
  z.object({
    activationMode: z.enum(["auto-export", "manual"]),
    configJson: z.record(z.unknown()),
    createdAt: z.string(),
    id: z.string(),
    sourceKey: z.string(),
    sourceSnapshotJson: z.record(z.unknown()),
    sourceType: z.enum(["docs", "blog", "landing-pages"]),
    status: z.enum(["inactive", "active", "blocked"]),
    updatedAt: z.string(),
  });

export const primeUiManagedSearchIndexSchema: z.ZodType<PrimeUiManagedSearchIndex> =
  z.object({
    createdAt: z.string(),
    documentCount: z.number().int().nonnegative(),
    id: z.string(),
    kind: z.enum(["default-runtime", "custom"]),
    lastErrorSummary: z.string().nullable(),
    lastFailedSyncAt: nullableDateStringSchema,
    lastSuccessfulSyncAt: nullableDateStringSchema,
    meilisearchIndexUid: z.string().nullable(),
    name: z.string(),
    publicSearchId: z.string().nullable(),
    queryRole: z.enum(["default-runtime", "manual"]),
    retiredAt: nullableDateStringSchema,
    slug: z.string(),
    sourceSelectionJson: z.record(z.unknown()),
    status: z.enum(["inactive", "syncing", "ready", "failed", "degraded"]),
    updatedAt: z.string(),
  });

export const primeUiManagedSearchJobSchema: z.ZodType<PrimeUiManagedSearchJob> =
  z.object({
    attemptCount: z.number().int().nonnegative(),
    createdAt: z.string(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    finishedAt: nullableDateStringSchema,
    id: z.string(),
    jobType: z.enum(["sync", "reindex", "recover"]),
    maxAttempts: z.number().int().nonnegative(),
    projectSearchIndexId: z.string(),
    requestedById: z.string().nullable(),
    requestedByType: z.enum(["export", "studio-user", "cli", "system"]),
    startedAt: nullableDateStringSchema,
    state: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
  });

export const primeUiManagedSearchSyncRunSchema: z.ZodType<PrimeUiManagedSearchSyncRun> =
  z.object({
    deletedDocumentCount: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative().nullable(),
    errorSummary: z.string().nullable(),
    extractedDocumentCount: z.number().int().nonnegative(),
    finishedAt: nullableDateStringSchema,
    id: z.string(),
    projectSearchIndexId: z.string(),
    projectSearchJobId: z.string(),
    startedAt: z.string(),
    status: z.enum(["running", "succeeded", "failed", "cancelled"]),
    writtenDocumentCount: z.number().int().nonnegative(),
  });

export const primeUiManagedSearchQueryProtectionSchema: z.ZodType<PrimeUiManagedSearchQueryProtectionState> =
  z.object({
    enabled: z.literal(true),
    rateLimit: z.object({
      enabled: z.literal(true),
      windowMs: z.number().int().positive(),
      maxRequests: z.number().int().positive(),
      activeBucketCount: z.number().int().nonnegative(),
      limitedRequestCount: z.number().int().nonnegative(),
      recentLimitedAt: nullableDateStringSchema,
    }),
    logging: z.object({
      enabled: z.literal(true),
      lastDayEventCount: z.number().int().nonnegative(),
      recentEventCount: z.number().int().nonnegative(),
      lastHourEventCount: z.number().int().nonnegative(),
      lastEventAt: nullableDateStringSchema,
    }),
    cache: z.object({
      enabled: z.literal(false),
      ttlMs: z.null(),
      reason: z.string(),
    }),
  });

export const primeUiManagedSearchRelevanceSettingsSchema: z.ZodType<PrimeUiManagedSearchRelevanceSettingsSummary> =
  z.object({
    displayedAttributes: z.array(z.string()),
    facetSearch: z.boolean(),
    filterableAttributes: z.array(z.string()),
    pagination: z.object({
      maxTotalHits: z.number().int().positive(),
    }),
    prefixSearch: z.enum(["indexingTime", "disabled"]),
    primaryKey: z.string(),
    proximityPrecision: z.enum(["byWord", "byAttribute"]),
    rankingRules: z.array(z.string()),
    searchableAttributes: z.array(z.string()),
    sortableAttributes: z.array(z.string()),
    typoTolerance: z.object({
      disableOnAttributes: z.array(z.string()),
      enabled: z.boolean(),
      minWordSizeForTypos: z.object({
        oneTypo: z.number().int().positive(),
        twoTypos: z.number().int().positive(),
      }),
    }),
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project/search.
 * Primary Studio runtime entrypoint: apps/studio/src/routes/api/v1/project/search/route.ts
 */
export const primeUiManagedSearchOverviewResponseSchema: z.ZodType<PrimeUiManagedSearchOverviewResult> =
  z.object({
    customIndexes: z.array(primeUiManagedSearchIndexSchema),
    defaultIndex: primeUiManagedSearchIndexSchema.nullable(),
    lastError: primeUiManagedSearchLastErrorSchema.nullable(),
    projectId: z.string(),
    queryProtection: primeUiManagedSearchQueryProtectionSchema,
    recentJobs: z.array(primeUiManagedSearchJobSchema),
    recentSyncRuns: z.array(primeUiManagedSearchSyncRunSchema),
    relevanceSettings: primeUiManagedSearchRelevanceSettingsSchema,
    runtimeModel: primeUiManagedSearchRuntimeModelSchema,
    runtimes: z.array(primeUiManagedSearchRuntimeSchema),
    search: primeUiManagedSearchRootSchema,
    sources: z.array(primeUiManagedSearchSourceSchema),
    success: z.literal(true),
  });

/**
 * Consumer-side runtime contract for POST/PATCH /api/v1/project/search/runtimes
 * and /api/v1/project/search/runtimes/:runtimeSlug.
 * Primary Studio runtime entrypoints:
 * - apps/studio/src/routes/api/v1/project/search/runtimes/route.ts
 * - apps/studio/src/routes/api/v1/project/search/runtimes/$runtimeSlug/route.ts
 */
export const primeUiManagedSearchRuntimeMutationResponseSchema: z.ZodType<PrimeUiManagedSearchRuntimeApiMutationResult> =
  z.object({
    success: z.literal(true),
    runtime: primeUiManagedSearchRuntimeSchema,
  });

/**
 * Consumer-side runtime contract for DELETE /api/v1/project/search/runtimes/:runtimeSlug.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/routes/api/v1/project/search/runtimes/$runtimeSlug/route.ts
 */
export const primeUiManagedSearchRuntimeDeleteResponseSchema: z.ZodType<PrimeUiManagedSearchRuntimeDeleteResult> =
  z.object({
    success: z.literal(true),
  });

/**
 * Consumer-side runtime contract for GET /api/v1/project/exports.
 * Primary Studio runtime entrypoint: apps/studio/src/tanstack/routes/api.v1.project.exports.ts
 * Legacy migration reference: apps/studio/src/app/api/v1/project/exports/route.ts
 */
export const primeUiExportsResponseSchema = z.object({
  exports: z.array(
    z.object({
      id: z.string(),
      status: primeUiExportStatusSchema,
      createdAt: z.string().datetime({ offset: true }),
    }),
  ),
});

/**
 * Consumer-side runtime contract for POST /api/v1/project/exports.
 * Primary Studio runtime entrypoint: apps/studio/src/tanstack/routes/api.v1.project.exports.ts
 * Legacy migration reference: apps/studio/src/app/api/v1/project/exports/route.ts
 */
export const primeUiCreateExportResponseSchema: z.ZodType<PrimeUiCreateExportResult> =
  z.object({
    export: primeUiPostExportPayloadExportSchema,
    pages: z.array(primeUiExportPageSchema),
  });

export const primeUiExportManifestSchema: z.ZodType<PrimeUiExportManifest> =
  primeUiCreateExportResponseSchema;

/**
 * Consumer-side runtime contract for
 * POST /api/v1/component-registry/components/:componentId/exports.
 * Primary Studio runtime entrypoint:
 * apps/studio/src/routes/api/v1/component-registry/components/$componentId/exports/route.ts
 */
export const primeUiComponentExportResponseSchema: z.ZodType<PrimeUiComponentExportManifest> =
  z.object({
    exportId: z.string(),
    export: primeUiPostExportPayloadExportSchema,
    block: primeUiComponentExportSelectedBlockSchema.optional(),
    component: primeUiComponentExportComponentSchema,
    page: primeUiComponentExportPageSchema,
    pages: z.array(primeUiComponentExportPageSchema),
  });

export const primeUiComponentExportManifestSchema: z.ZodType<PrimeUiComponentExportManifest> =
  primeUiComponentExportResponseSchema;

export function parsePrimeUiExportManifest(
  value: unknown,
): PrimeUiExportManifest {
  if (!value || typeof value !== "object") {
    throw new Error("Export manifest is invalid.");
  }

  const maybe = value as Record<string, unknown>;
  if (!maybe.export || typeof maybe.export !== "object") {
    throw new Error("Export manifest export payload is invalid.");
  }

  const parsed = primeUiExportManifestSchema.safeParse(value);
  if (!parsed.success) {
    const hasPageIssue = parsed.error.issues.some(
      (issue) => issue.path[0] === "pages",
    );

    throw new Error(
      hasPageIssue
        ? "Export manifest pages payload is invalid."
        : "Export manifest does not match expected schema.",
    );
  }

  return parsed.data;
}

export function parsePrimeUiComponentExportManifest(
  value: unknown,
): PrimeUiComponentExportManifest {
  if (!value || typeof value !== "object") {
    throw new Error("Component export manifest is invalid.");
  }

  const maybe = value as Record<string, unknown>;
  if (!maybe.component || typeof maybe.component !== "object") {
    throw new Error("Component export manifest component payload is invalid.");
  }

  const parsed = primeUiComponentExportManifestSchema.safeParse(value);
  if (!parsed.success) {
    throw new Error(
      `Component export manifest is invalid: ${parsed.error.issues
        .map((issue) => `${issue.path.join(".") || "<root>"} ${issue.message}`)
        .join("; ")}`,
    );
  }

  return parsed.data;
}
